import type { FieldValue } from "firebase/firestore";

/** Values that can cross a JSON API boundary. Decode unknown input before use. */
export type JsonValue = string | number | boolean | null | JsonValue[] | JsonObject;
export interface JsonObject { [key: string]: JsonValue; }

export interface TimestampValue {
  toDate(): Date;
  toMillis?(): number;
}
export interface SerializedTimestamp {
  seconds?: number;
  nanoseconds?: number;
  _seconds?: number;
  _nanoseconds?: number;
}
// Optimistic rating rows can retain serverTimestamp sentinels until the server
// snapshot replaces them; date consumers must narrow before reading a date.
export type DateValue = Date | TimestampValue | SerializedTimestamp | FieldValue | string | number | null;

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/** Unlike Array.isArray's library signature, keep unchecked elements unknown. */
export const isUnknownArray = (value: unknown): value is unknown[] => Array.isArray(value);

/** Object.entries otherwise erases the value type of interfaces to any. */
export const unknownEntries = (value: object): [string, unknown][] => Object.entries(value);

export const isTimestamp = (value: unknown): value is TimestampValue =>
  isRecord(value) && typeof value.toDate === "function";

export const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : isRecord(error) ? String(error.message ?? "") : String(error ?? "");

export const errorCode = (error: unknown): string =>
  isRecord(error) && typeof error.code === "string" ? error.code : "";

export const errorName = (error: unknown): string =>
  error instanceof Error ? error.name : isRecord(error) ? String(error.name ?? "") : "";

export interface CodedError extends Error {
  code?: string;
  status?: number;
  retryAfterSeconds?: number;
}

/** Normalize supported Firebase/cache timestamp representations without coercing objects. */
export const dateValueToDate = (value: unknown): Date | null => {
  let date: Date;
  if (value instanceof Date) date = value;
  else if (isTimestamp(value)) date = value.toDate();
  else if (isRecord(value) && (typeof value.seconds === "number" || typeof value._seconds === "number")) {
    const seconds = typeof value.seconds === "number" ? value.seconds : value._seconds;
    const nanoseconds = typeof value.nanoseconds === "number" ? value.nanoseconds
      : typeof value._nanoseconds === "number" ? value._nanoseconds : 0;
    date = new Date(Number(seconds) * 1000 + nanoseconds / 1_000_000);
  } else if (typeof value === "string" || typeof value === "number") date = new Date(value);
  else return null;
  return date instanceof Date && Number.isFinite(date.getTime()) ? date : null;
};

export const dateValueToMillis = (value: unknown, fallback = 0): number =>
  dateValueToDate(value)?.getTime() ?? fallback;
